import { useMutation } from "@apollo/client/react";
import { DSButton } from "@components/design-system/molecules";
import {
  useAuth,
  useTeam,
  usePremiumFeature,
  useTemplates,
} from "@hooks";
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import AutoTemplatesList from "./AutoTemplatesList";
import TemplatesFreePlanState from "./empty-states/TemplatesFreePlanState";
import { createNewTemplate } from "./utils";
import KnowledgeIsPawer from "../../assets/imgs/knowledge-is-pawer.svg";
import EmptyStateImg from "../../assets/imgs/template-empty-state.svg";
import {
  UPDATE_AUTO_TEMPLATE,
  CREATE_AUTO_TEMPLATE,
} from "../../graphql/mutations/AutoTemplates";
import { trackOperation } from "../../utils/operationTracking";
import { alert } from "../common/Alert";
import EmphasisBanner from "../common/EmphasisBanner";
import EmptyState from "../common/loading/EmptyState";

const AutoTemplatesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();

  const { currentTeam } = useTeam();
  const { userUuid } = useAuth();
  const {
    teamAndOrgDefaultTemplates,
    sharedTemplates,
    ownedTemplates,
    allTemplates: templates,
    loadingTemplates: loading,
    refetchTemplates: refetch,
    refetchDefaultTemplates,
  } = useTemplates();

  const [updateAutoTemplate] = useMutation(UPDATE_AUTO_TEMPLATE);
  const [createAutoTemplate] = useMutation(CREATE_AUTO_TEMPLATE);

  const refetchAllTemplates = () => {
    refetch();
    refetchDefaultTemplates();
  };

  const favoriteTemplates = [
    ...ownedTemplates.filter((template) => template.isFavourite),
    ...sharedTemplates.filter((template) => template.isFavourite),
  ];

  const templatesToShow = ownedTemplates;

  const soapTemplates = templatesToShow.filter(
    (template) => template.templateType === "SOAP",
  );
  const customTemplates = templatesToShow.filter(
    (template) => template.templateType === "CUSTOM",
  );

  const handleTemplateClick = (template) => {
    const templateOwnerUuid = template.user.uuid;
    if (templateOwnerUuid === userUuid) {
      navigate(`/templates/edit/${template.uuid}`);
    } else {
      const scrollY = window.scrollY;
      const returnTo = `/templates?scrollY=${scrollY}`;
      navigate(
        `/templates/shared/${
          template.uuid
        }?returnTo=${encodeURIComponent(returnTo)}`,
      );
    }
  };

  const handleFavouriteToggle = (template) =>
    trackOperation("template-favorite-toggled", async (ctx) => {
      const newFavouriteState = !template?.isFavourite;

      ctx.addProperties({
        templateUuid: template?.uuid,
        templateOwnerUuid: template?.user?.uuid,
        isFavourited: newFavouriteState,
        source: "templates dashboard",
      });

      await updateAutoTemplate({
        variables: {
          input: {
            templateUuid: template?.uuid,
            isFavourite: newFavouriteState,
          },
        },
      });

      let message = "";
      if (template.isFavourite) {
        message =
          "You've removed this template from your favorites. It will no longer appear at the top of your templates list.";
      } else {
        message =
          "You've favorited this template. It will now appear at the top of your templates list.";
      }
      alert("success", message);
      refetchAllTemplates();
    });

  const handleCreateNewTemplate = async () => {
    const templateUuid = await createNewTemplate(
      "SOAP",
      true,
      createAutoTemplate,
    );
    navigate(`/templates/edit/${templateUuid}?isNew=true`);
  };

  useEffect(() => {
    if (!loading) {
      const scrollY = new URLSearchParams(location.search).get(
        "scrollY",
      );
      if (scrollY) {
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(scrollY, 10),
            behavior: "smooth",
          });
        }, 100);
      }
    }
  }, [loading, location.search]);

  if (loading) {
    return null;
  }

  if (!hasFeatureAccess(UserPermissions.ADAPTIVE_TEMPLATES_ENABLED)) {
    return <TemplatesFreePlanState />;
  }

  return (
    <div className="relative min-h-[80vh] flex flex-col">
      <div
        className={
          "flex justify-between gap-md flex-col sm:flex-row sm:items-start"
        }
      >
        <div className={"flex flex-col justify-center"}>
          <h1 data-testid="adaptive-templates-title">
            Adaptive Templates
          </h1>
          <h4 className="text-sm text-secondary my-sm font-medium">
            Scribenote can <i>Scribe Like You</i>. Adaptive Templates
            allow Scribenote to adapt to your existing templates,
            preferences, and writing style.
          </h4>
        </div>

        <DSButton
          onClick={handleCreateNewTemplate}
          type="button"
          dataTestId="new-adaptive-template-button"
        >
          + New Adaptive Template
        </DSButton>
      </div>
      {templates.length === 0 && !loading && (
        <EmphasisBanner>
          <div className="flex flex-row items-center justify-between w-full px-md">
            <div className="flex flex-col items-start space-y-4">
              <h4 className="max-w-[600px] text-secondary">
                Ready to make Scribenote your personalized AI Scribe?
                Read our docs to learn how to set up your first
                adaptive template.
              </h4>
              <a
                href="https://docs.scribenote.com/en/articles/10496596"
                target="_blank"
                rel="noreferrer"
                className="self-start bg-brand text-white px-md py-sm rounded-full hover:opacity-90 hover:text-white transition-all"
              >
                Read Docs →
              </a>
            </div>

            <img src={KnowledgeIsPawer} className="w-48" />
          </div>
        </EmphasisBanner>
      )}

      {/* Return null if loading because the query is really fast and having a normal loader would look jumpy */}
      {loading ? null : templates.length > 0 ? (
        <>
          <div
            className="space-y-10 bg-brand-subtle rounded-large p-lg"
            data-testid="templates-main-content"
          >
            <AutoTemplatesList
              title="Favorites"
              subtitle="These templates will appear at the top of your recording dropdown."
              templates={favoriteTemplates}
              templateType="FAVOURITES"
              onTemplateClick={handleTemplateClick}
              onFavouriteToggle={handleFavouriteToggle}
              dataTestId="favorite-templates-list"
              hideAddNewButton={true}
              refetch={refetchAllTemplates}
            />

            <AutoTemplatesList
              title="SOAP Format"
              templates={soapTemplates}
              templateType="SOAP"
              onTemplateClick={handleTemplateClick}
              onFavouriteToggle={handleFavouriteToggle}
              refetch={refetchAllTemplates}
            />

            <AutoTemplatesList
              title="Freeform"
              templates={customTemplates}
              templateType="CUSTOM"
              onTemplateClick={handleTemplateClick}
              onFavouriteToggle={handleFavouriteToggle}
              refetch={refetchAllTemplates}
            />
            {teamAndOrgDefaultTemplates.length > 0 && (
              <AutoTemplatesList
                title="Team Defaults"
                subtitle={`Templates that have been set as defaults for your team, ${currentTeam?.name}.`}
                templates={teamAndOrgDefaultTemplates}
                templateType="TEAM_DEFAULTS"
                onTemplateClick={handleTemplateClick}
                onFavouriteToggle={handleFavouriteToggle}
                dataTestId="team-default-templates-list"
                hideAddNewButton={true}
                refetch={refetchAllTemplates}
              />
            )}
            {sharedTemplates.length > 0 && (
              <AutoTemplatesList
                title="Shared with you"
                subtitle="Templates that have been shared with you."
                templates={sharedTemplates}
                templateType="SHARED"
                onTemplateClick={handleTemplateClick}
                onFavouriteToggle={handleFavouriteToggle}
                dataTestId="shared-templates-list"
                hideAddNewButton={true}
                refetch={refetchAllTemplates}
              />
            )}
          </div>
        </>
      ) : (
        <div className="grow flex items-center justify-center">
          <EmptyState
            title="No templates here yet."
            img={EmptyStateImg}
          />
        </div>
      )}
    </div>
  );
};

export default AutoTemplatesPage;
